import { Delete, Check } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { useState, useEffect } from 'react';

interface NumericKeypadProps {
    onValueChange: (value: number) => void;
    onConfirm: () => void;
    value: number;
    className?: string;
    allowDecimals?: boolean;
}

export function NumericKeypad({ onValueChange, onConfirm, value, className, allowDecimals = true }: NumericKeypadProps) {
    // Local state to handle "0." or "1." intermediate states
    const [displayValue, setDisplayValue] = useState(value.toString());

    useEffect(() => {
        setDisplayValue(value.toString());
    }, [value]);

    const handleNumberClick = (num: number) => {
        let newValueStr = displayValue;

        // If current value is just "0" (and not "0."), replace it
        if (displayValue === '0') {
            newValueStr = num.toString();
        } else {
            newValueStr = displayValue + num.toString();
        }

        // Check for max 3 decimals
        if (newValueStr.includes('.')) {
            const [, decimals] = newValueStr.split('.');
            if (decimals && decimals.length > 3) return;
        }

        // Prevent too long numbers (arbitrary limit of 7 chars to fit screen)
        if (newValueStr.length > 7) return;

        setDisplayValue(newValueStr);
        const parsed = parseFloat(newValueStr);
        if (!isNaN(parsed)) {
            onValueChange(parsed);
        }
    };

    const handleDotClick = () => {
        if (!allowDecimals) return;
        if (!displayValue.includes('.')) {
            setDisplayValue(displayValue + '.');
        }
    };

    const handleClear = () => {
        setDisplayValue('0');
        onValueChange(0);
    };

    const handleBackspace = () => {
        let newValueStr = displayValue.slice(0, -1);
        if (newValueStr === '' || newValueStr === '-') {
            newValueStr = '0';
        }

        setDisplayValue(newValueStr);
        const parsed = parseFloat(newValueStr);
        if (!isNaN(parsed)) {
            onValueChange(parsed);
        }
    };

    return (
        <div className={cn("w-64 p-2", className)}>
            {/* Display Screen */}
            <div className="mb-3 bg-gray-100 border-2 border-gray-300 rounded-lg h-16 flex items-center justify-end px-4">
                <span className="text-3xl font-mono font-bold text-gray-800 tracking-wider">
                    {displayValue}
                </span>
            </div>

            <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <Button
                        key={num}
                        variant="outline"
                        className="h-14 text-2xl font-semibold"
                        onClick={() => handleNumberClick(num)}
                    >
                        {num}
                    </Button>
                ))}
                {allowDecimals ? (
                    <Button
                        variant="outline"
                        className="h-14 text-2xl font-semibold"
                        onClick={handleDotClick}
                    >
                        .
                    </Button>
                ) : (
                    <div />
                )}
                <Button variant="outline" className="h-14 text-2xl font-semibold" onClick={() => handleNumberClick(0)}>0</Button>
                <Button variant="outline" className="h-14" onClick={handleBackspace}>
                    <Delete className="w-6 h-6" />
                </Button>

                <div className="col-span-3 flex gap-2 mt-1">
                    <Button variant="destructive" className="h-14 flex-1 text-lg font-semibold" onClick={handleClear}>C</Button>
                    <Button className="flex-[2] h-14 text-xl bg-green-600 hover:bg-green-700" onClick={onConfirm}>
                        <Check className="w-6 h-6 mr-2" /> Valider
                    </Button>
                </div>
            </div>
        </div>
    );
}
